import {
  deleteAllowUrl,
  getAllowedUrls,
  sendAllowedUrl,
  updateAllowedUrl,
} from '@api/allowedUrlsAPI';
import {
  ThemedPrimaryBtn,
  ThemedSecondaryBtn,
} from '@themes/defaultThemeProps';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardIcon from 'components/Card/CardIcon';
import Button from 'components/CustomButtons/Button.js';
import CustomInput from 'components/CustomInput/CustomInput';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Table from 'components/Table/Table';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
// material ui icons
import { selectSessionToken } from '@features/session/sessionSlice';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import EnvironmentIcon from '@mui/icons-material/SettingsInputComponent';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { getStackTrace, log } from '@src/Log.init';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { isValidURL } from '../../utils';

const AllowedUrls = (props) => {
  const { classes, plain = false, setDirtyTabStatus } = props;
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState('');
  const [updateUrl, setUpdateUrl] = useState('');
  const [deleteUrl, setDeleteUrl] = useState('');
  const [isDelete, setIsDelete] = useState(false);
  const [urlList, setUrlList] = useState([]);
  const [urlError, setUrlError] = useState('');
  const updateInputRef = useRef();
  const sessionToken = useSelector(selectSessionToken);

  const addUrl = async (url) => {
    setLoading(true);
    await sendAllowedUrl(sessionToken, url)
      .then((res) => {
        handleGetData();
        setUrl('');
      })
      .finally(() => {
        if (setDirtyTabStatus) {
          setDirtyTabStatus(false);
        }
        if (props?.onSuccessTab) {
          props?.onSuccessTab(props.tab.name);
        }
        setLoading(false);
      })
      .catch((err) => {
        log.error({
          message_type: 'SystemError',
          message: 'POST allowed url failed',
          meta_data: getStackTrace(),
        });
        toast.error('An error occurred while ' + 'trying to save the changes', {
          position: toast.POSITION.BOTTOM_CENTER,
          autoClose: 2000,
          hideProgressBar: true,
        });
      });
  };
  const [tableData, setTableData] = useState([]);

  const actionButton = (Icon, color, handler, key) => {
    return (
      <Button
        key={key}
        color={color}
        simple
        className={classes.actionButton}
        onClick={() => handler(key)}
      >
        <Icon className={classes.icon} />
      </Button>
    );
  };

  const handleEditUrl = (key) => {
    handleOpen();
    setIsDelete(false);
    setUpdateUrl(urlList[key]);
  };

  const handleDeleteUrl = (key) => {
    handleOpen();
    setIsDelete(true);
    setDeleteUrl(urlList[key]);
  };

  const getListData = () => {
    let listData = urlList?.map((entry, key) => {
      let urlListData = [];
      let actionButtons = [];
      urlListData.push(entry?.origin);
      actionButtons.push(actionButton(Edit, 'warning', handleEditUrl, key));
      actionButtons.push(actionButton(Delete, 'warning', handleDeleteUrl, key));
      urlListData.push(actionButtons);

      return urlListData;
    });
    setTableData(listData);
  };

  useLayoutEffect(() => {
    getListData();
  }, [urlList]);

  const handleGetData = useCallback(async () => {
    await getAllowedUrls(sessionToken)
      .then(async (response) => {
        await setUrlList(response);
      })
      .catch((err) => {
        log.error({
          message_type: 'SystemError',
          message: 'Get allowed urls failed',
          meta_data: getStackTrace(),
        });
      });
  }, [urlList]);

  useEffect(() => {
    handleGetData();
  }, []);

  const [showAlert, setShowAlert] = useState(false);

  const handleClose = () => {
    setShowAlert(false);
  };

  const handleOpen = (key) => {
    setShowAlert(true);
  };

  const handleAction = async () => {
    if (isDelete) {
      await deleteAllowUrl(sessionToken, deleteUrl?._id)
        .then((res) => {
          handleGetData();
          handleClose();
        })
        .catch((err) => {
          log.error({
            message_type: 'SystemError',
            message: 'Delete allowed url failed',
            meta_data: getStackTrace(),
          });
        });
    } else {
      await updateAllowedUrl(sessionToken, {
        ...updateUrl,
        ['origin']: updateInputRef.current.value,
      })
        .then((res) => {
          handleGetData();
          handleClose();
        })
        .catch((err) => {
          log.error({
            message_type: 'SystemError',
            message: 'Delete allowed url failed',
            meta_data: getStackTrace(),
          });
        });
    }
  };

  const Alert = ({ open }) => {
    return (
      <Dialog
        fullWidth={true}
        maxWidth='sm'
        open={open}
        aria-labelledby='form-dialog-title'
      >
        <DialogTitle id='form-dialog-title'>
          {isDelete ? `Delete Allowed URL from list` : `Update Allowed URL`}
        </DialogTitle>
        <DialogContent>
          {isDelete ? (
            <DialogContentText id='alert-dialog-description'>
              Deleting this entry cannot be undone!
              <br />
              Press Delete to confirm, or Cancel to cancel.
            </DialogContentText>
          ) : (
            <GridContainer spacing={2}>
              <GridItem xs={12}>
                <CustomInput
                  labelText='URL'
                  id='updateUrl'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: updateUrl?.origin,
                    inputRef: updateInputRef,
                    autoComplete: 'off',
                  }}
                />
              </GridItem>
            </GridContainer>
          )}
        </DialogContent>
        <DialogActions>
          <ThemedSecondaryBtn onClick={() => handleClose()} color='secondary'>
            Cancel
          </ThemedSecondaryBtn>
          <ThemedPrimaryBtn onClick={() => handleAction()} color='primary'>
            {isDelete ? 'Delete' : 'Update'}
          </ThemedPrimaryBtn>
        </DialogActions>
      </Dialog>
    );
  };

  const content = (
    <>
      <GridContainer>
        <GridItem xs={12}>
          <p>
            External domains allowed to interact with the InStore APIs for CORS
          </p>
        </GridItem>
      </GridContainer>
      <GridContainer alignItems='center'>
        <GridItem xs={6}>
          <CustomInput
            helperText={
              urlError !== '' ? urlError ?? 'An error has occurred' : false
            }
            // success={urlError === ""}
            error={urlError !== ''}
            labelText='URL'
            id='url'
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              disabled: !props?.tab.active ?? false,
              value: url,
              onChange: (event) => {
                if (setDirtyTabStatus) {
                  setDirtyTabStatus(true);
                }
                if (isValidURL(event.target.value)) {
                  setUrlError('');
                } else {
                  setUrlError('The URL you typed is not valid');
                }
                setUrl(event.target.value);
              },
              autoComplete: 'off',
            }}
          />
        </GridItem>
        <GridItem xs={3}>
          <Button
            color='rose'
            onClick={() => addUrl(url)}
            className={classes.registerButton}
            disabled={
              urlError !== '' || url === '' || !(props?.tab.active ?? false)
            }
          >
            + ADD ALLOWED URL
          </Button>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          {urlList.length > 0 ? (
            <Table tableHead={['URL', 'Actions']} tableData={tableData} />
          ) : null}
        </GridItem>
      </GridContainer>
    </>
  );

  return (
    <>
      <Alert open={showAlert} />
      <GridItem xs={12} sm={12} md={12}>
        {plain ? (
          content
        ) : (
          <Card>
            <CardHeader color='rose' icon>
              <CardIcon color='rose'>
                <EnvironmentIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Allowed URLs</h4>
            </CardHeader>
            <CardBody>{content}</CardBody>
          </Card>
        )}
      </GridItem>
    </>
  );
};

export default AllowedUrls;
